import {Injectable} from '@angular/core';
import {MatIconRegistry} from '@angular/material/icon';
import {DomSanitizer} from '@angular/platform-browser';

/**
 * Cómo hay que pintar el `icono` de un item de menú: como ligature de la fuente
 * Material (`<mat-icon>{{ ligature }}</mat-icon>`) o como SVG ya registrado
 * (`<mat-icon [svgIcon]="name">`).
 */
export type MenuIconSpec =
    | { kind: 'font', ligature: string }
    | { kind: 'svg', name: string };

/** Carpeta de los SVG propios del menú, dentro de `src/assets`. */
export const ICONOS_SVG_BASE_PATH = 'assets/iconos/';

/** Namespace propio en `MatIconRegistry`, para no pisar los iconos que registren otras pantallas. */
export const MENU_ICON_NAMESPACE = 'cnc-menu';

/** Icono con el que el menú pinta una sección que no tiene SVG propio. */
export const ICONO_MENU_POR_DEFECTO = 'generico.svg';

/**
 * Nombre de fichero admisible: sin barras, sin `..` suelto, sin protocolo y sin
 * espacios. Cualquier otra cosa haría que el registry pidiese el SVG fuera de
 * `assets/iconos/`, y el valor viene de la API (lo teclea un empleado en
 * Admin → Items).
 */
const NOMBRE_SVG = /^[A-Za-z0-9._-]+\.svg$/i;

/**
 * Decide si el `icono` de un item de menú es un nombre de la fuente Material o
 * un SVG propio de `assets/iconos/`, y en el segundo caso lo registra —una sola
 * vez— en `MatIconRegistry`.
 *
 * El discriminador es la extensión `.svg`. Los valores que ya existen en
 * producción son ligatures sin extensión, así que ninguno cambia de
 * comportamiento.
 *
 * Portado de `BeiMenuIconResolver` de `@bei/ui`, donde vive la versión
 * canónica y configurable. Aquí la carpeta es fija porque cnc no consume la
 * librería.
 */
@Injectable({providedIn: 'root'})
export class MenuIconResolver {

    private readonly _registrados = new Set<string>();

    constructor(private readonly _registry: MatIconRegistry, private readonly _sanitizer: DomSanitizer) {
    }

    /**
     * @param icono Valor del campo `icono` del item, tal cual llega de la API.
     * @param fallback Ligature a usar cuando el item no trae icono, o cuando
     *                 trae un nombre de fichero que no se puede cargar.
     */
    public resolve(icono: string | null | undefined, fallback: string): MenuIconSpec {
        const valor = (icono || '').trim();
        if (valor === '') return {kind: 'font', ligature: fallback};
        if (!valor.toLowerCase().endsWith('.svg')) return {kind: 'font', ligature: valor};
        if (!NOMBRE_SVG.test(valor)) return {kind: 'font', ligature: fallback};

        if (!this._registrados.has(valor)) {
            this._registry.addSvgIconInNamespace(
                MENU_ICON_NAMESPACE,
                valor,
                this._sanitizer.bypassSecurityTrustResourceUrl(ICONOS_SVG_BASE_PATH + valor)
            );
            this._registrados.add(valor);
        }
        return {kind: 'svg', name: MENU_ICON_NAMESPACE + ':' + valor};
    }

    /**
     * Si el icono es un fichero de `assets/iconos/` en vez de un nombre de la
     * fuente Material. Las plantillas lo usan para elegir entre pintar la
     * ligature o `[svgIcon]`.
     */
    public esSvg(icono: string | null | undefined): boolean {
        return this.resolve(icono, '').kind === 'svg';
    }

    /** Nombre registrado del SVG para el `[svgIcon]` de la plantilla, o cadena vacía si no lo es. */
    public svgIcono(icono: string | null | undefined): string {
        const spec = this.resolve(icono, '');
        return spec.kind === 'svg' ? spec.name : '';
    }

    /**
     * Cómo pinta el menú el icono de una sección:
     *
     * - termina en `.svg` → el fichero de `assets/iconos/`;
     * - cualquier otro valor → ligature de Material, como siempre;
     * - nada, o un nombre de fichero que se sale de la carpeta → el de
     *   respaldo. Lo segundo no cae a Material a propósito: es un icono propio
     *   mal escrito, y la fuente dejaría el texto crudo escrito en el menú.
     */
    public iconoDeMenu(icono: string | null | undefined): MenuIconSpec {
        const valor = (icono || '').trim();
        if (valor === '' || valor.toLowerCase().endsWith('.svg')) {
            const spec = this.resolve(valor, '');
            return spec.kind === 'svg' ? spec : {kind: 'svg', name: this.svgIcono(ICONO_MENU_POR_DEFECTO)};
        }
        return {kind: 'font', ligature: valor};
    }
}
